/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useRef, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { useEditorMainContext } from "../../contexts/EditorMainContext";
import { IResourcePathBreadcrumbItem } from "./ResourcePathBreadcrumbsTypes";
import { ResourcePathBreadcrumbsPanel } from "./ResourcePathBreadcrumbsPanel";
import { IResourceNode } from "../ResourceList";

export interface IResourcePathBreadcrumbsItemProps {
	/** Breadcrumb item data */
	item: IResourcePathBreadcrumbItem;
	/** Whether this is the last item in the list */
	isLast?: boolean;
	/** Whether to enable popup navigation */
	enablePopup?: boolean;
	/** Callback when a resource is selected from popup */
	onResourceSelect?: (resourceId: string) => void;
	/** Class name */
	className?: string;
}

export const ResourcePathBreadcrumbsItem: React.FC<IResourcePathBreadcrumbsItemProps> = ({
	item,
	isLast,
	enablePopup = true,
	onResourceSelect,
	className
}) => {
	const [ isPanelOpened, setIsPanelOpened ] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>();
	const { resourceService, commandsService } = useEditorMainContext();

	// Check if there are any resources at this path level
	const hasResources = useMemo(() => {
		if (!enablePopup) {
			return false;
		}
		const resources = resourceService.getResourceListByBasePath(item.path);
		return resources.length > 0;
	}, [ enablePopup, item.path, resourceService ]);

	const handleClick = useCallback(
		(ev: React.MouseEvent) => {
			ev.stopPropagation();

			if (enablePopup && hasResources) {
				setIsPanelOpened((s) => !s);
			}
		},
		[ enablePopup, hasResources ]
	);

	const handlePanelClose = useCallback(() => {
		setIsPanelOpened(false);
	}, []);

	const handleResourceSelect = useCallback(
		(node: IResourceNode) => {
			// If it's a directory, don't close the panel - user can navigate into it
			if (node.resourceType === RESOURCE_TYPES.DIRECTORY) {
				return;
			}

			// Close the panel and open the resource
			setIsPanelOpened(false);

			if (onResourceSelect) {
				onResourceSelect(node.id);
			} else {
				commandsService.execute("resource.open", node.id).catch((err) => {
					console.error("Failed to open resource:", err);
				});
			}
		},
		[ onResourceSelect, commandsService ]
	);

	const hasPopup = enablePopup && hasResources;
	const baseClass = BEM("edt-breadcrumbs-item", null, {
		clickable: hasPopup,
		"is-current": item.isCurrent,
		"full-width": isLast
	});

	return (
		<button
			ref={buttonRef}
			className={`${baseClass.toClassName()} ${className || ""}`}
			onClick={handleClick}
			title={item.label}
		>
			{item.label}
			{hasPopup && (
				<ResourcePathBreadcrumbsPanel
					targetRef={buttonRef}
					isOpened={isPanelOpened}
					basePath={item.path}
					selectedId={item.resourceId}
					onSelect={handleResourceSelect}
					onClose={handlePanelClose}
				/>
			)}
		</button>
	);
};

ResourcePathBreadcrumbsItem.displayName = "ResourcePathBreadcrumbsItem";
